import {
  Cartesian2,
  Cartesian3,
  Color,
  DeveloperError,
  Math as CesiumMath,
  Matrix4,
  ParticleSystem,
  SphereEmitter,
} from "@cesium/engine";
import { DitapViewer, ConstantUrl, Utils } from "../../..";

/**
 * 강수 또는 강설을 생성하고 표시하는 클래스.
 * Class for creating and displaying rain or snow.
 *
 * @class
 * @constructor
 * @param {object} precipitationOptions 다음과 같은 속성을 가진 객체.</br>Object with the following properties:
 * @param {DitapViewer} precipitationOptions.viewer 사용할 Ditap Viewer 인스턴스.(viewer의 shouldAnimate: true 필수)</br>The DitapViewer instance to use.
 * @param {Number} precipitationOptions.type 강수 유형 1은 비, 2는 눈. (기본값: 1).</br>Type of precipitation. 1 for rain, 2 for snow. (default: 1).
 * @param {Number} precipitationOptions.amount 강수량. (기본값: 25, 최소값: 0, 최대값: 50).</br>Amount of precipitation. (default: 25, minimun: 0, maximun: 50).
 * @param {Number} precipitationOptions.longitude 강수의 중심 경도. </br>Longitude of the center of precipitation.
 * @param {Number} precipitationOptions.latitude 강수의 중심 위도. </br>Latitude of the center of precipitation.
 * @param {Number} precipitationOptions.radius 강수가 생성될 범위의 반지름 (m).(기본값: 10000, 최소값:5000,  최대값: 10000).</br>Radius of the area affected by precipitation (in meters). (default: 10000, minimum:5000, maximum: 10000).
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Precipitaion' 참조
 *
 * // ditapviewer의 shouldAnimate: true 필수
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *     shouldAnimate: true,
 * });
 *
 * // 강수 효과 인스턴스 생성
 * const precipitation = new Ditap.Precipitation({
 *     viewer: viewer,
 *     type: 1,
 *     amount: 50,
 *     longitude: 126.978275264,
 *     latitude: 37.566642192,
 *     radius: 10000
 * });
 *
 * // 카메라 이동
 * viewer.camera.setView({
 *     destination: Ditap.Cartesian3.fromDegrees(126.978275264, 37.566642192, 200),
 *     orientation: {
 *         heading: Ditap.Math.toRadians(0),
 *         pitch: Ditap.Math.toRadians(-10),
 *         roll: 0,
 *     },
 * });
 *
 * // 강수 효과 시작
 * precipitation.start();
 *
 */
class Precipitation {
  constructor(precipitationOptions) {
    if (precipitationOptions.viewer instanceof DitapViewer) {
      /**
       * 사용할 Ditap Viewer 인스턴스.</br>
       * The DitapViewer instance to use.
       * @type {DitapViewer}
       *
       * */
      this.viewer = precipitationOptions.viewer;
      this.scene = precipitationOptions.viewer.scene;
    }

    const availAbleOptions = this.checkPrecipitationOptions(
      precipitationOptions
    );

    this.type = availAbleOptions.type;
    this.amount = availAbleOptions.amount;

    /**
     * 강수의 중심 경도.</br>
     * Longitude of the center of precipitation.
     * @type {Number}
     */
    this.longitude = availAbleOptions.longitude;
    /**
     * 강수의 중심 위도.</br>
     * Latitude of the center of precipitation.
     * @type {Number}
     * */
    this.latitude = availAbleOptions.latitude;
    /**
     * 강수가 생성될 범위의 반지름 (m). (기본값: 10000, 최소값:5000,  최대값: 10000).</br>
     * Radius of the area affected by precipitation (in meters). (default: 10000, minimum:5000, maximum: 10000).
     * @type {Number}
     * */
    this.radius = availAbleOptions.radius;

    this.position = Cartesian3.fromDegrees(this.longitude, this.latitude);

    this._isStart = false;

    this.originAtmosphereOption = {
      skyAtmosphere: {
        hueShift: this.scene.skyAtmosphere.hueShift,
        saturationShift: this.scene.skyAtmosphere.saturationShift,
        brightnessShift: this.scene.skyAtmosphere.brightnessShift,
      },
      fog: {
        density: this.scene.fog.density,
        minimumBrightness: this.scene.fog.minimumBrightness,
      },
    };

    if (this.amount < 20) {
      this.snowImageUrl = Utils.getUrlFromBase64(
        ConstantUrl.SNOW_PARTICLE_PHASE_1_URL
      );
    } else {
      this.snowImageUrl = Utils.getUrlFromBase64(ConstantUrl.SNOW_PARTICLE_URL);
    }
    this.rainImageUrl = Utils.getUrlFromBase64(ConstantUrl.RAIN_PARTICLE_URL);
    this._isStart = false;
  }

  /**
   * 강수 유형. 1은 비, 2는 눈. (기본값: 1).</br>
   * Type of precipitation. 1 for rain, 2 for snow. (default: 1).
   * @type {Number}
   * */
  get type() {
    return this._type;
  }

  set type(value) {
    this._type = value;
    if (this._isStart) {
      this.start();
    }
  }

  /**
   * 강수량. (기본값: 25, 최소값: 0, 최대값: 50).</br>
   * Amount of precipitation. (default: 25, minimun: 0, maximun: 50).
   * @type {Number}
   * */
  get amount() {
    return this._amount;
  }

  set amount(value) {
    if (value < 20) {
      this.snowImageUrl = Utils.getUrlFromBase64(
        ConstantUrl.SNOW_PARTICLE_PHASE_1_URL
      );
    } else if (value < 30) {
      this.snowImageUrl = Utils.getUrlFromBase64(
        ConstantUrl.SNOW_PARTICLE_PHASE_2_URL
      );
    } else {
      this.snowImageUrl = Utils.getUrlFromBase64(ConstantUrl.SNOW_PARTICLE_URL);
    }
    this._amount = value;
    if (this._isStart) {
      this.start();
    }
  }

  /*
   * 비 효과 시작.</br>
   * Starts the rain effect.
   */
  startRain() {
    this.stopRain();
    this.stopSnow();

    this.setAtmosphereCloudy();
    this.rainPrimitive = this.createParticle();
    this.scene.primitives.add(this.rainPrimitive);
    this._isStart = true;
  }

  /*
   * 눈 효과 시작.</br>
   * Starts the snow effect.
   */
  startSnow() {
    this.stopRain();
    this.stopSnow();

    this.setAtmosphereCloudy();
    this.snowPrimitive = this.createSnowParticle();
    this.scene.primitives.add(this.snowPrimitive);
    this._isStart = true;
  }

  /**
   * 강수 효과 시작.</br>
   * Starts the precipitation effect.
   */
  start() {
    this._isStart = true;
    if (this.isRain()) {
      this.startRain();
    } else {
      this.startSnow();
    }
  }

  /**
   * 강수 효과 중지.</br>
   * Stops the precipitation effect.
   */
  stop() {
    this.setOriginAtmosphere();

    if (this.isRain()) {
      this.stopRain();
    } else {
      this.stopSnow();
    }
  }

  /**
   * 객체를 파괴하고 관련 리소스를 해제합니다. </br>
   * Destroys the object and releases associated resources.
   */
  destroy() {
    // 구름 효과 중지
    this.stop();

    // 뷰어 연결 해제
    this.viewer = undefined;
  }

  /**
   * 비 효과 중지.</br>
   * Stops the rain effect.
   */
  stopRain() {
    if (
      this.scene.primitives.contains(this.rainPrimitive) &&
      this.rainPrimitive
    ) {
      this.scene.primitives.remove(this.rainPrimitive);
    }
    this._isStart = false;
  }

  /**
   * 눈 효과 중지.</br>
   * Stops the snow effect.
   */
  stopSnow() {
    if (
      this.scene.primitives.contains(this.snowPrimitive) &&
      this.snowPrimitive
    ) {
      this.scene.primitives.remove(this.snowPrimitive);
    }
    this._isStart = false;
  }

  /**
   * 현재 강수 유형이 비인지 확인.</br>
   * Check if the current precipitation type is rain.
   *
   * @returns {boolean} 강수가 비인 경우 true를 반환, 그렇지 않으면 false를 반환.</br>Returns true if the precipitation is rain, otherwise returns false.
   */
  isRain() {
    if (this.type === 1) {
      return true;
    }
    return false;
  }

  /**
   * 현재 강수 유형이 눈인지 확인.</br>
   * Check if the current precipitation type is snow.
   *
   * @returns {boolean} 강수가 눈인 경우 true를 반환, 그렇지 않으면 false를 반환.</br>Returns true if the precipitation is snow, otherwise returns false.
   */
  isSnow() {
    if (this.type === 2) {
      return true;
    }
    return false;
  }

  /*
   * 강수 타입에 따라 하늘을 흐린 상태로 설정.<br>
   * Set the sky to cloudy state based on the precipitation type.
   */
  setAtmosphereCloudy() {
    if (this.isRain()) {
      this.scene.skyAtmosphere.hueShift = -0.97;
      this.scene.skyAtmosphere.saturationShift = 0.25;
      this.scene.skyAtmosphere.brightnessShift = -0.4;
      this.scene.fog.density = 0.00025;
      this.scene.fog.minimumBrightness = 0.01;
    } else if (this.isSnow()) {
      this.scene.skyAtmosphere.hueShift = -0.8;
      this.scene.skyAtmosphere.saturationShift = -0.7;
      this.scene.skyAtmosphere.brightnessShift = -0.33;
      this.scene.fog.density = 0.001;
      this.scene.fog.minimumBrightness = 0.8;
    }
  }

  setOriginAtmosphere() {
    this.scene.skyAtmosphere.hueShift = this.originAtmosphereOption.skyAtmosphere.hueShift;
    this.scene.skyAtmosphere.saturationShift = this.originAtmosphereOption.skyAtmosphere.saturationShift;
    this.scene.skyAtmosphere.brightnessShift = this.originAtmosphereOption.skyAtmosphere.brightnessShift;
    this.scene.fog.density = this.originAtmosphereOption.fog.density;
    this.scene.fog.minimumBrightness = this.originAtmosphereOption.fog.minimumBrightness;
  }

  /**
   * 비 입자 생성.</br>
   * Create rain particles.
   *
   * @returns {ParticleSystem} 비 입자 시스템을 반환.</br>Returns the rain particle system.
   */
  createRainParticle() {
    // const rainParticleSize = 15.0;
    const rainRadius = this.radius;
    const rainImageSize = new Cartesian2(937, 487);
    let rainGravityScratch = new Cartesian3();

    const rainUpdate = (particle, dt) => {
      rainGravityScratch = Cartesian3.normalize(
        particle.position,
        rainGravityScratch
      );
      rainGravityScratch = Cartesian3.multiplyByScalar(
        rainGravityScratch,
        -1050.0,
        rainGravityScratch
      );

      particle.position = Cartesian3.add(
        particle.position,
        rainGravityScratch,
        particle.position
      );

      const distance = Cartesian3.distance(
        this.viewer.scene.camera.position,
        particle.position
      );

      if (distance > rainRadius) {
        particle.endColor.alpha = 0.0;
        particle.startColor.alpha = 0.0;
      } else {
        particle.startColor.alpha = 0.2;
        particle.endColor.alpha = 1.0 / (distance / rainRadius + 0.1);
      }
    };

    const emisstionRate = this.calculateRate(this.amount, rainRadius);

    const particle = new ParticleSystem({
      modelMatrix: new Matrix4.fromTranslation(this.position),
      speed: -1.0,
      lifetime: 15.0,
      emitter: new SphereEmitter(rainRadius), // 반지름 미터
      startScale: 1.0,
      endScale: 0.0,
      image: this.rainImageUrl,
      emissionRate: emisstionRate,
      startColor: Color.WHITE.withAlpha(0.0),
      endColor: Color.WHITE.withAlpha(0.2),
      imageSize: rainImageSize,
      updateCallback: rainUpdate,
    });

    return particle;
  }

  /**
   * 눈 입자 생성.</br>
   * Create snow particles.
   *
   * @returns {ParticleSystem} 눈 입자 시스템을 반환.</br>Returns the snow particle system.
   */
  createSnowParticle() {
    // snow
    // const snowParticleSize = 12.0;
    const snowRadius = this.radius;
    let minimumSnowImageSize = new Cartesian2(900, 400);
    let maximumSnowImageSize = new Cartesian2(900 * 1.5, 400 * 1.5);
    if (this.amount < 20) {
      minimumSnowImageSize = new Cartesian2(900, 400);
      maximumSnowImageSize = new Cartesian2(900 * 1.2, 400 * 1.2);
    }

    let snowGravityScratch = new Cartesian3();
    const snowUpdate = (particle, dt) => {
      snowGravityScratch = Cartesian3.normalize(
        particle.position,
        snowGravityScratch
      );
      Cartesian3.multiplyByScalar(
        snowGravityScratch,
        CesiumMath.randomBetween(-0.5, -10.0),
        snowGravityScratch
      );
      particle.velocity = Cartesian3.add(
        particle.velocity,
        snowGravityScratch,
        particle.velocity
      );
      const distance = Cartesian3.distance(
        this.viewer.scene.camera.position,
        particle.position
      );
      if (distance > snowRadius) {
        particle.endColor.alpha = 0.0;
      } else {
        particle.endColor.alpha = 1.0 / (distance / snowRadius + 0.1);
      }
    };

    const emisstionRate = this.calculateRate(this.amount, snowRadius);

    const particle = new ParticleSystem({
      modelMatrix: new Matrix4.fromTranslation(this.position),
      minimumSpeed: -1.0,
      maximumSpeed: 0.0,
      lifetime: 15.0,
      emitter: new SphereEmitter(snowRadius),
      startScale: 0.5,
      endScale: 1.0,
      image: this.snowImageUrl,
      emissionRate: emisstionRate,
      startColor: Color.WHITE.withAlpha(0.0),
      endColor: Color.WHITE.withAlpha(1.0),
      minimumImageSize: minimumSnowImageSize,
      maximumImageSize: maximumSnowImageSize,
      updateCallback: snowUpdate,
    });

    return particle;
  }

  /**
   * 강수 입자 생성.</br>
   * Create precipitation particles based on the type of precipitation.
   *
   * @returns {ParticleSystem} 생성된 강수 입자 시스템 반환.</br>Returns the generated precipitation particle system.
   */
  createParticle() {
    let particle;
    if (this.isRain()) {
      particle = this.createRainParticle();
    } else if (this.isSnow()) {
      particle = this.createSnowParticle();
    }

    return particle;
  }

  /**
   * 강수량을 계산.</br>
   * Calculate the precipitation rate.
   *
   * @param {Number} amount 강수량입니다. </br>Amount of precipitation.
   * @returns {Number} result 계산된 강수량. </br>Calculated precipitation rate.
   */
  calculateRate(amount) {
    // 반지름 최대값 : 10000
    // 초당 빗방울 최대값: 5000
    // 강수량 최대값: 50

    let result = 5000 * (amount / 50);
    if (this.isSnow()) {
      result *= 0.1;
    }
    return result;
  }
  // calculateRate(amount, radius) {
  //   const pi = Math.PI;
  //   // 반지름 최대값 : 10000
  //   // 초당 빗방울 최대값: 5000
  //   // 초당 눈방울 최대값: 900
  //   // 강수량 최대값: 50

  //   const currentArea = pi * radius ** 2;
  //   let k = 5000 / (pi * 10000 ** 2 * 50); // k값 계산
  //   let maxRate = 5000;
  //   if (this.isSnow()) {
  //     k = 900 / (pi * 10000 ** 2 * 50);
  //     maxRate = 900;
  //   }
  //   let result = k * currentArea * amount; // 결과값 계산

  //   if (result > maxRate) {
  //     result = maxRate;
  //   }

  //   return result;
  // }

  /**
   * 강수 옵션 객체의 유효성 검사.</br>
   * Validate the precipitation options object.
   *
   * @param {object} precipitationOptions 다음과 같은 속성을 가진 객체.</br>Object with the following properties:
   * @param {Number} precipitationOptions.type 강수 유형. 1은 비, 2는 눈. (기본값: 1).</br>Type of precipitation. 1 for rain, 2 for snow. (default: 1).
   * @param {Number} precipitationOptions.amount 강수량. (기본값: 25, 최소값: 0, 최대값: 50).</br>Amount of precipitation. (default: 25, minimun: 0, maximun: 50).
   * @param {Number} precipitationOptions.longitude 강수의 중심 경도.</br>Longitude of the center of precipitation.
   * @param {Number} precipitationOptions.latitude 강수의 중심 위도.</br>Latitude of the center of precipitation.
   * @param {Number} precipitationOptions.radius 강수가 생성될 범위의 반지름 (m). (기본값: 10000, 최소값:5000,  최대값: 10000).<br>Radius of the area affected by precipitation (in meters). (default: 10000, minimum:5000, maximum: 10000).
   * @returns {object} precipitationOptions 유효성 검사가 완료되 강수 옵션.</br> Precipitation options after validation.
   * @throws {DeveloperError} 강수 옵션의 유효성이 확인되지 않으면 발생.</br>Throws if the precipitation options are invalid.
   */
  checkPrecipitationOptions(precipitationOptions) {
    let type = precipitationOptions.type;
    let amount = precipitationOptions.amount;
    let radius = precipitationOptions.radius;
    // 반지름 1000이하일 경우 비가 내리는 영역의 구가 작아지면서 낮은 하늘에서 비가 내림
    // 반지름 10000이상일 경우 버벅임
    const longitude = precipitationOptions.longitude;
    const latitude = precipitationOptions.latitude;
    if (!this.viewer.clock.shouldAnimate) {
      throw new DeveloperError(
        "'shouldAnimate' value of the viewer must be true."
      );
    }
    if (!(type === 1 || type === 2)) {
      type = 1;
    }
    if (typeof amount !== "number") {
      amount = 25;
    }
    if (amount > 50) {
      amount = 50;
    }
    if (amount < 0) {
      amount = 0;
    }
    if (typeof longitude !== "number") {
      throw new DeveloperError("longitude invalid");
    }
    if (typeof latitude !== "number") {
      throw new DeveloperError("latitude invalid");
    }
    if (typeof radius !== "number") {
      radius = 10000;
    }
    if (radius < 0) {
      radius = 0;
    }
    if (radius > 10000) {
      radius = 10000;
    }
    if (radius < 5000) {
      radius = 5000;
    }

    precipitationOptions.type = type;
    precipitationOptions.amount = amount;
    precipitationOptions.radius = radius;

    return precipitationOptions;
  }
}
export default Precipitation;
